import { useEffect, type FC, Fragment } from 'react'
import { observer } from 'mobx-react-lite'
import { Typography, SpinnerLoader, Scrollbar } from 'shared/ui'
import { OutboundDropdownCard } from 'entities/Message/ui/OutboundDropdownCard/OutboundDropdownCard'
import { type OutboundSource } from 'entities/OutboundSource/model/OutboundSource'
import { type SearchDropdownOutboundSourceStore } from 'widgets/SearchDropdown/store/searchDropdownOutboundSourceStore'
import { getLimitReachedTooltip } from 'widgets/SearchDropdown/constants/limitReachedTooltip'
import styles from './styles.module.scss'

type IOutboundSourcesDropdownContentItemsProps = {
  store: SearchDropdownOutboundSourceStore
  onChange?: (item: OutboundSource) => void
}

export const OutboundSourcesDropdownContentItems: FC<IOutboundSourcesDropdownContentItemsProps> =
  observer(({ store, onChange }) => {
    const {
      loading,
      hasItems,
      itemsListKeys,
      init,
      options,
      selectedItemsKeys,
      selectTypeItem,
      isSelected,
      getItemByKey,
      maxLimit,
      selectedItemsCount,
    } = store
    const isMaxLimitReached = maxLimit !== null && selectedItemsCount >= maxLimit

    const handleSelect = (item: OutboundSource) => {
      onChange?.(item)
      selectTypeItem(item)
    }

    const checkIsSelected = (key: string) => {
      return isSelected(key)
    }

    useEffect(() => {
      init()
    }, [])

    return (
      <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={290}>
        <div className={styles.list}>
          {[...selectedItemsKeys, ...itemsListKeys].map((key, index) => {
            const item = getItemByKey(key)
            if (!item) return

            const isDisabled = isMaxLimitReached && !isSelected(item.key)
            const tooltipProps = isMaxLimitReached ? getLimitReachedTooltip() : undefined
            const inputCheckboxProps = options?.selectedMode
              ? {
                  checked: checkIsSelected(item.key),
                  onChecked: () => handleSelect(item),
                }
              : undefined

            return (
              <Fragment key={item.key}>
                <OutboundDropdownCard
                  InputCheckboxProps={inputCheckboxProps}
                  onChange={handleSelect}
                  item={item}
                  tooltipProps={tooltipProps}
                  disabled={isDisabled}
                />
                {index === selectedItemsKeys.length - 1 && selectedItemsKeys.length > 0 && (
                  <div className={styles.divider}></div>
                )}
              </Fragment>
            )
          })}
          {!hasItems && !loading && (
            <div className={styles.emptyList}>
              <Typography variant={'body-rg-regular'} ariaLabel={'Not found'}>
                Not found
              </Typography>
            </div>
          )}
          {loading && (
            <div className={styles.spinner}>
              <SpinnerLoader ariaLabel={'OutboundSourcesDropdownContentItems'} />
            </div>
          )}
        </div>
      </Scrollbar>
    )
  })
